import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CoreDirectivesModule } from '@core/directives/directives';
import { NgbTooltip, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { FlatpickrModule } from 'angularx-flatpickr';
import { SelectFieldComponent } from 'app/main/pages/contract-control/editable-fields/select-field/select-field.component';
import { TextFieldComponent } from 'app/main/pages/contract-control/editable-fields/text-field/text-field.component';
import { IDocumentDetailRootDTO, VisibilityEnum } from 'app/main/pages/documents/dto/document-details.dto';
import { ExtractSenderDetailPipe } from 'app/pipes/documents-right-sidebar-pipes/extract-sender-details.pipe';
import { MomentPipe } from 'app/pipes/moment.pipe';
import { UserTimeZoneFormatPipe } from 'app/pipes/user-timezone-format.pipe';
import { TranslocoRootModule } from 'app/transloco-root.module';
import { FieldActionEvent } from '../dynamic-field.component';
import { environment } from 'environments/environment';
import { Clipboard } from '@angular/cdk/clipboard';
import { delay, of, Subscription, take } from 'rxjs';

@Component({
  selector: 'app-visibility-field',
  standalone: true,
  imports: [
    CommonModule,
    CoreDirectivesModule,
    TranslocoRootModule,
    NgSelectModule,
    TextFieldComponent,
    SelectFieldComponent,
    NgbTooltipModule,
    ExtractSenderDetailPipe,
    FlatpickrModule,
    MomentPipe,
    UserTimeZoneFormatPipe,
    FormsModule,
    ReactiveFormsModule
  ],
  templateUrl: './visibility-field.component.html',
  styleUrl: './visibility-field.component.scss'
})
export class VisibilityFieldComponent {

  @Input() formGroup!: FormGroup;
  @Input() documentData: IDocumentDetailRootDTO;
  @Input() canEdit: boolean = true;
  @Output() actionEmit = new EventEmitter<FieldActionEvent>();
  @ViewChild('tooltip') tooltip: NgbTooltip;



  private timerSubscription: Subscription;
  public VisibilityEnum = VisibilityEnum;


  constructor(
    private _copyClipboard: Clipboard,
  ) {}

  get isPrivate(): boolean {
    return this.formGroup.get('visibility')?.value === VisibilityEnum.Private;
  }

  get visibilityText(): string {
    return this.isPrivate 
      ? 'Text for private link (Documents Section) - private'
      : 'Text for public link (Documents Section) - public to anyone';
  }

  /**
     * Copies the document's shareable URL to the clipboard and displays a tooltip
     * to indicate the action. The tooltip is automatically closed after a delay.
     *
     * The URL is constructed using the application's base URL and the document's
     * template ID and document number.
     *
     * @remarks
     * - The tooltip is opened when the method is invoked and closed after 1 second.
     * - The URL is copied to the clipboard using the `_copyToClipBoard$` service.
     *
     * @example
     * // Assuming `environment.appUrl` is "https://example.com/" and
     * // `this.documentData` contains the necessary template and document details:
     * // URL copied: "https://example.com/shareable/{templateId}/{documentNumber}"
     *
     * @returns void
     */
    copyDocUrl(): void {
      this.tooltip.open();
      const url = `${environment.appUrl}shareable/${this.documentData.template.id}/${this.documentData.template_details.document_number}`;
      this._copyClipboard.copy(url);
      this.timerSubscription = of(null).pipe(
        take(1),
        delay(1000)
      ).subscribe(() => {
        this.tooltip.close();
      });
    }


  updateVisibility() {
    if (!this.canEdit) {
      return;
    }
    this.actionEmit.emit({ action: 'updateVisibility' });
  }
}
